import { inject, Injectable } from '@angular/core';
import type { Auth0Client } from '@auth0/auth0-spa-js';
import { environment } from '@environments/environment';
import { CurrentLanguageService } from '@shared/services/language/current-language.service';
import { LogsService } from '@shared/services/logs';

export const AUTH_CALLBACK_PATH = 'auth/callback';
export const SESSION_UNTIL_KEY = 'socooc.espaceClientSessionUntil';

interface AppState {
  target: string;
}

/** Browser only: the Auth0 SDK needs `window.crypto` and `location`, which the server render does not have. */
@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private readonly logs = inject(LogsService);
  private readonly language = inject(CurrentLanguageService).language;
  private client?: Promise<Auth0Client>;

  readonly isAvailable = !!environment.AUTH0;

  /** Leaves the site for the Auth0 page, then comes back through the callback and ends on `target`. */
  async loginWithRedirect(target: string, signup = false): Promise<void> {
    const client = await this.getClient();
    await client.loginWithRedirect<AppState>({
      appState: { target },
      authorizationParams: signup ? { screen_hint: 'signup' } : {},
    });
  }

  /** True until the id token of this site's last login expires: a login made on the espace client itself is not seen. */
  hasSession(): boolean {
    return this.isAvailable && Number(localStorage.getItem(SESSION_UNTIL_KEY)) > Date.now();
  }

  /** Never resolves, so the app never renders on the callback page it is about to leave. */
  async handleRedirectCallback(): Promise<never> {
    let target = '/';
    if (this.isAvailable && new URLSearchParams(location.search).has('state')) {
      try {
        const client = await this.getClient();
        const { appState } = await client.handleRedirectCallback<AppState>();
        target = appState?.target ?? target;
        // Only the expiry is kept: the site uses no token, and any script of its origin can read localStorage.
        const claims = await client.getIdTokenClaims();
        if (claims?.exp) localStorage.setItem(SESSION_UNTIL_KEY, String(claims.exp * 1000));
      } catch (error) {
        this.logs.error('AuthenticationService', 'Auth0 redirect callback failed', error);
      }
    }
    location.replace(target);
    return new Promise<never>(() => undefined);
  }

  // Loaded on demand: the SDK weighs 63 KB gzipped, which only a visitor who logs in should pay.
  private getClient(): Promise<Auth0Client> {
    this.client ??= import('@auth0/auth0-spa-js').then(({ Auth0Client }) => {
      const config = environment.AUTH0;
      if (!config) {
        throw new Error('Auth0 is not configured for this environment');
      }
      return new Auth0Client({
        domain: config.domain,
        clientId: config.clientId,
        authorizationParams: {
          redirect_uri: `${location.origin}/${AUTH_CALLBACK_PATH}`,
          audience: config.audience,
          ui_locales: this.language.locale.split('-')[0],
        },
      });
    });
    return this.client;
  }
}
